<template>
  <div :class="['checkbox-wrapper', { 'no-wrapper': noWrapper, 'is-filter-active': !!modelValue }]">
    <label v-if="label && !noWrapper" :for="id" class="checkbox-label-text">
      <template v-if="label.trim()">{{ label }}</template>
      <template v-else>&nbsp;</template>
      <span v-if="required" class="required-indicator" aria-label="requis">*</span>
    </label>
    <label 
      :for="id" 
      :class="['checkbox-label', `variant-${variant}`, { 
        'is-checked': modelValue,
        'is-disabled': disabled,
        'has-error': error
      }]"
      @click="!disabled && handleClick"
    >
      <input
        ref="inputRef"
        :id="id"
        type="checkbox"
        :checked="modelValue"
        :disabled="disabled"
        :required="required"
        :aria-label="ariaLabel || (noWrapper && !label ? labelText : undefined)"
        :aria-describedby="errorId || (hint ? hintId : undefined)"
        :aria-invalid="error ? 'true' : undefined"
        :data-testid="testId"
        class="checkbox-input"
        @change="handleChange"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />
      <span v-if="labelText || $slots.default" class="checkbox-text">
        <slot>{{ labelText }}</slot>
      </span>
    </label>
    <span v-if="error && !noWrapper" :id="errorId" class="error-text" role="alert" aria-live="polite">{{ error }}</span>
    <span v-if="hint && !error && !noWrapper" :id="hintId" class="hint-text">{{ hint }}</span>
  </div>
</template>

<script setup>
import { ref, computed, defineExpose } from 'vue'

const inputRef = ref(null)

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: null
  },
  labelText: {
    type: String,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  hint: {
    type: String,
    default: null
  },
  id: {
    type: String,
    default: () => `checkbox-${Math.random().toString(36).substr(2, 9)}`
  },
  noWrapper: {
    type: Boolean,
    default: false
  },
  ariaLabel: {
    type: String,
    default: null
  },
  testId: {
    type: String,
    default: null
  },
  variant: {
    type: String,
    default: 'default', // 'default' | 'filter' | 'card' | 'plain'
    validator: (value) => ['default', 'filter', 'card', 'plain'].includes(value)
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'change'])

const errorId = computed(() => props.error ? `${props.id}-error` : null)
const hintId = computed(() => props.hint ? `${props.id}-hint` : null)

const handleChange = (e) => {
  emit('update:modelValue', e.target.checked)
  emit('change', e.target.checked)
}

const handleClick = (e) => {
  // Prevent double toggle when clicking on the label
  if (e.target === inputRef.value) {
    return
  }
}

defineExpose({
  inputRef
})
</script>

<style scoped>
.checkbox-wrapper {
  margin-bottom: var(--spacing-md);
  display: flex;
  flex-direction: column;
}

.checkbox-wrapper.no-wrapper {
  margin-bottom: 0;
}

.checkbox-wrapper.no-wrapper .checkbox-label-text {
  display: none;
}

/* Alignement dans FilterBar */
:deep(.filter-bar-content) .checkbox-wrapper.no-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.checkbox-label-text {
  display: block;
  margin-bottom: var(--spacing-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  user-select: none;
  transition: all var(--transition-base);
  padding: 0.6rem 0.9rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-bg-secondary);
  min-height: 40px;
  line-height: var(--line-height-normal, 1.5);
}

.checkbox-label:hover:not(.is-disabled) {
  background: var(--color-bg-hover);
}

.checkbox-label.is-checked {
  background: var(--color-bg-secondary);
  border-color: var(--color-primary);
}

.checkbox-label.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.checkbox-label.has-error {
  border-color: var(--color-danger);
}

.checkbox-input {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--color-primary);
  flex-shrink: 0;
  margin: 0;
  margin-top: 0 !important;
}

.checkbox-input:disabled {
  cursor: not-allowed;
}

.checkbox-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary);
  flex: 1;
  transition: color var(--transition-base);
}

.checkbox-label.is-checked .checkbox-text {
  color: var(--color-primary-dark);
}

.checkbox-label.is-disabled .checkbox-text {
  color: var(--color-text-secondary);
}

.error-text {
  display: block;
  margin-top: var(--spacing-xs);
  font-size: 0.875rem;
  color: var(--color-danger);
}

.hint-text {
  display: block;
  margin-top: var(--spacing-xs);
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
}

.required-indicator {
  color: var(--color-danger);
  margin-left: 2px;
}

/* Variant: filter (for filter bars) */
.checkbox-label.variant-filter {
  width: 100%;
}

:deep(.filter-bar-content) .checkbox-wrapper.no-wrapper {
  flex: 1;
  min-width: 200px;
}

/* Variant: card (for card-like checkboxes) */
.checkbox-label.variant-card {
  padding: var(--spacing-md);
  border: 2px solid var(--color-border);
}

.checkbox-label.variant-card:hover:not(.is-disabled) {
  background: var(--color-bg-hover);
}

.checkbox-label.variant-card.is-checked {
  border-color: var(--color-primary);
  background: var(--color-bg-secondary);
}

/* Variant: plain — la case et son libellé seuls, sans cadre ni fond : pour une
   case accolée à un champ qu'elle qualifie (ex. « Confirmée avec le client »
   sous l'e-mail de l'analyse du besoin), qu'un cadre ferait lire comme un champ
   de plus. */
.checkbox-label.variant-plain {
  min-height: 0;
  padding: 0;
  border: none;
  background: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-normal);
}

.checkbox-label.variant-plain:hover:not(.is-disabled),
.checkbox-label.variant-plain.is-checked {
  background: none;
}

.checkbox-label.variant-plain .checkbox-input {
  width: 16px;
  height: 16px;
}

@media (max-width: 768px) {
  .checkbox-label.variant-filter {
    min-width: 100%;
  }
}
</style>
